import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// ── Helpers ────────────────────────────────────────────────────────────────

const APP_TYPE_LABELS = {
  moebius_replacement: 'Moebius Replacement',
  moebius_bleed:       'Moebius Bleed (Precipitation)',
  copper_wastewater:   'Copper Wastewater',
  copper_refinery:     'Copper Refinery Bleed',
  ewaste:              'Ewaste Refining (Cu+PM)',
};

const TECH_LABELS = {
  competitor: 'Competitor',
  emew:       'emew',
};

const CAT_COLORS = {
  labour:      '#4A90D9',
  energy:      '#F6AD55',
  reagents:    '#68D391',
  maintenance: '#FC8181',
  lab:         '#B794F4',
  revenue:     '#4FD1C5',
};

// ── Cost Model Row (inline edit) ───────────────────────────────────────────

function CostModelRow({ item, onSaved }) {
  const [editing, setEditing]   = useState(false);
  const [saving,  setSaving]    = useState(false);
  const [draft,   setDraft]     = useState({
    label:   item.label,
    formula: item.formula,
    notes:   item.notes || '',
    enabled: item.enabled === 1,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/cost-models/${item.id}`, {
        label:   draft.label,
        formula: draft.formula,
        notes:   draft.notes,
        enabled: draft.enabled,
      });
      toast.success(`Saved "${draft.label}"`);
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft({
      label:   item.label,
      formula: item.formula,
      notes:   item.notes || '',
      enabled: item.enabled === 1,
    });
    setEditing(false);
  };

  const toggleEnabled = async () => {
    try {
      await api.put(`/admin/cost-models/${item.id}`, {
        label:   item.label,
        formula: item.formula,
        notes:   item.notes || '',
        enabled: !item.enabled,
      });
      toast.success(item.enabled ? 'Line item disabled' : 'Line item enabled');
      onSaved();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const rowStyle = {
    opacity:    item.enabled ? 1 : 0.45,
    transition: 'opacity 0.15s',
  };

  return (
    <tr style={rowStyle}>
      {/* Category badge */}
      <td>
        <span style={{
          display:       'inline-block',
          padding:       '0.15rem 0.55rem',
          borderRadius:  '999px',
          fontSize:      '0.72rem',
          fontWeight:    600,
          background:    (CAT_COLORS[item.category] || '#CBD5E0') + '22',
          color:         CAT_COLORS[item.category]  || '#718096',
          textTransform: 'capitalize',
          whiteSpace:    'nowrap',
        }}>
          {item.is_revenue ? '★ ' : ''}{item.category}
        </span>
      </td>

      {/* Label */}
      <td style={{ minWidth: 160 }}>
        {editing ? (
          <input
            className="form-input"
            style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem' }}
            value={draft.label}
            onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
          />
        ) : (
          <span style={{ fontSize: '0.875rem' }}>{item.label}</span>
        )}
      </td>

      {/* Formula */}
      <td style={{ minWidth: 260 }}>
        {editing ? (
          <textarea
            className="form-input"
            rows={2}
            style={{ fontSize: '0.78rem', fontFamily: 'monospace', padding: '0.3rem 0.5rem', resize: 'vertical' }}
            value={draft.formula}
            onChange={e => setDraft(d => ({ ...d, formula: e.target.value }))}
          />
        ) : (
          <code style={{
            display:    'block',
            fontSize:   '0.78rem',
            background: '#F7FAFC',
            padding:    '0.25rem 0.4rem',
            borderRadius: '4px',
            color:      '#2D3748',
            wordBreak:  'break-all',
          }}>
            {item.formula}
          </code>
        )}
      </td>

      {/* Notes */}
      <td style={{ minWidth: 140 }}>
        {editing ? (
          <input
            className="form-input"
            style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem' }}
            placeholder="Optional notes"
            value={draft.notes}
            onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
          />
        ) : (
          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
            {item.notes || '—'}
          </span>
        )}
      </td>

      {/* Actions */}
      <td style={{ whiteSpace: 'nowrap' }}>
        {editing ? (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving}
              style={{ fontSize: '0.78rem', padding: '0.3rem 0.65rem' }}
            >
              {saving ? '…' : 'Save'}
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={handleCancel}
              style={{ fontSize: '0.78rem', padding: '0.3rem 0.65rem' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setEditing(true)}
              style={{ fontSize: '0.78rem', padding: '0.3rem 0.65rem' }}
            >
              Edit
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={toggleEnabled}
              style={{
                fontSize:    '0.78rem',
                padding:     '0.3rem 0.65rem',
                color:       item.enabled ? '#E53E3E' : '#38A169',
                borderColor: item.enabled ? '#E53E3E' : '#38A169',
              }}
            >
              {item.enabled ? 'Disable' : 'Enable'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Cost Models Section ────────────────────────────────────────────────────

function CostModels() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null); // "appType|tech"

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/admin/cost-models');
      setItems(res.data.data);
    } catch {
      toast.error('Failed to load cost models');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Group: { [appType]: { [tech]: [items] } }
  const grouped = {};
  for (const item of items) {
    if (!grouped[item.app_type]) grouped[item.app_type] = {};
    if (!grouped[item.app_type][item.technology]) grouped[item.app_type][item.technology] = [];
    grouped[item.app_type][item.technology].push(item);
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading cost models…</div>;

  return (
    <div>
      {Object.entries(grouped).map(([appType, techMap]) => (
        <div key={appType} style={{ marginBottom: '1.5rem' }}>
          <div style={{
            padding:      '0.75rem 1.25rem',
            background:   'var(--color-dark-blue)',
            color:        '#fff',
            borderRadius: '8px 8px 0 0',
            fontSize:     '0.9rem',
            fontWeight:   700,
            letterSpacing: '0.02em',
          }}>
            {APP_TYPE_LABELS[appType] || appType}
          </div>

          {Object.entries(techMap).map(([tech, rows]) => {
            const key    = `${appType}|${tech}`;
            const isOpen = openKey === key;

            return (
              <div key={tech} style={{ border: '1px solid #E2E8F0', borderTop: 'none' }}>
                {/* Tech header / accordion trigger */}
                <button
                  onClick={() => setOpenKey(isOpen ? null : key)}
                  style={{
                    width:      '100%',
                    textAlign:  'left',
                    padding:    '0.65rem 1.25rem',
                    background: isOpen ? '#EBF8FF' : '#F7FAFC',
                    border:     'none',
                    cursor:     'pointer',
                    display:    'flex',
                    alignItems: 'center',
                    gap:        '0.5rem',
                    fontSize:   '0.875rem',
                    fontWeight: 600,
                    color:      'var(--color-dark-blue)',
                    borderBottom: isOpen ? '1px solid #BEE3F8' : 'none',
                    transition: 'background 0.1s',
                  }}
                >
                  <span style={{
                    display:      'inline-block',
                    width:        '6px',
                    height:       '6px',
                    borderRadius: '50%',
                    background:   tech === 'emew' ? '#38A169' : '#E53E3E',
                    flexShrink:   0,
                  }} />
                  {TECH_LABELS[tech] || tech}
                  <span style={{ marginLeft: 'auto', fontSize: '0.78rem', fontWeight: 400, color: '#718096' }}>
                    {rows.length} line items · {rows.filter(r => r.enabled).length} active
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#A0AEC0' }}>{isOpen ? '▲' : '▼'}</span>
                </button>

                {isOpen && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', minWidth: 700 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 110 }}>Category</th>
                          <th style={{ width: 160 }}>Label</th>
                          <th>Formula</th>
                          <th style={{ width: 150 }}>Notes</th>
                          <th style={{ width: 140 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(item => (
                          <CostModelRow key={item.id} item={item} onSaved={fetchItems} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Legend */}
      <div style={{
        marginTop:  '0.75rem',
        fontSize:   '0.78rem',
        color:      'var(--color-text-secondary)',
        lineHeight: '1.6',
      }}>
        <strong>Formula variables:</strong> constants (from Constants table) + user inputs + computed virtuals:
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>ag_kg_per_year</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>cu_kg_per_year</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>au_kg_per_year</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>moebius_cells</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>moebius_fte_computed</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>emew_fte_computed</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>monthly_repair_actual</code>,
        {' '}<code style={{ background: '#EDF2F7', padding: '0 3px', borderRadius: '3px' }}>moebius_total_ag_inventory_kg</code>.
        {' '}★ = revenue item (subtracts from emew net cost).
      </div>
    </div>
  );
}

// ── Main Admin Page ────────────────────────────────────────────────────────

export default function Admin() {
  const { user } = useAuth();
  const [users,    setUsers]    = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [section,  setSection]  = useState('users'); // 'users' | 'cost-models'

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;

    if (!email.endsWith('@emew.com')) {
      toast.error('Only @emew.com emails allowed');
      return;
    }

    try {
      await api.post('/admin/users', { email });
      toast.success(`Added ${email}`);
      setNewEmail('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add user');
    }
  };

  const handleRemove = async (id, email) => {
    if (!window.confirm(`Remove access for ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(`Removed ${email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove user');
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>You do not have admin access.</p>
      </div>
    );
  }

  const tabStyle = (active) => ({
    padding:      '0.5rem 1.25rem',
    borderRadius: '6px',
    border:       'none',
    cursor:       'pointer',
    fontWeight:   active ? 700 : 500,
    fontSize:     '0.875rem',
    background:   active ? 'var(--color-dark-blue)' : 'transparent',
    color:        active ? '#fff' : 'var(--color-text-secondary)',
    transition:   'all 0.15s',
  });

  return (
    <div>
      {/* Page header */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: 'var(--color-dark-blue)' }}>
          Admin Console
        </h2>
        <p style={{ margin: '0 0 1rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
          Manage user access and cost model formulae.
        </p>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.25rem', background: '#F7FAFC', borderRadius: '8px', padding: '0.25rem', width: 'fit-content' }}>
          <button style={tabStyle(section === 'users')} onClick={() => setSection('users')}>
            User Access
          </button>
          <button style={tabStyle(section === 'cost-models')} onClick={() => setSection('cost-models')}>
            Cost Models
          </button>
        </div>
      </div>

      {/* ── User Access ─────────────────────────────────────────────────── */}
      {section === 'users' && (
        <>
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.375rem' }}>
                  Add User
                </label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="name@emew.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                Add User
              </button>
            </form>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
            ) : (
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Added By</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>
                        <span className={`status-badge ${u.is_admin ? 'status-active' : 'status-pending'}`}>
                          {u.is_admin ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--color-text-secondary)' }}>{u.added_by || '—'}</td>
                      <td>
                        {u.email === 'alex.barshai@emew.com' ? (
                          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>Protected</span>
                        ) : (
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ color: '#E53E3E', borderColor: '#E53E3E' }}
                            onClick={() => handleRemove(u.id, u.email)}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── Cost Models ─────────────────────────────────────────────────── */}
      {section === 'cost-models' && (
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: 'var(--color-dark-blue)' }}>
            Cost Model Formulae
          </h3>
          <p style={{ margin: '0 0 1.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            Edit labels, formulae, and notes for each cost line item. Use <strong>Disable</strong> to
            exclude a line item from calculations without deleting it.
            Formula changes take effect on the next assessment submission.
          </p>
          <CostModels />
        </div>
      )}
    </div>
  );
}
